import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { gradeApi } from '../api.js';
import { useAuth } from '../auth/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import './sidebar.scss';

const SideBar = ({ activePage }) => {
  const { user, isLoading, logout } = useAuth();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [regrades, setRegrades] = useState(false);
  const [tokenUsage, setTokenUsage] = useState({
    credits_used: 0,
    credit_limit: 100
  });
  const [tokenUsageLoading, setTokenUsageLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  useEffect(() => {
    if (user && user.session_token) {
      const getRegrades = async () => {
        try {
          const response = await gradeApi.get({
            path: `/api/has-regrade-requests/`,
            config: { headers: { Authorization: `Bearer ${user.session_token}` } }
          });
          setRegrades(response.data['has_regrade_requests']);
        } catch (error) {
          console.log(error);
        }
      };

      const getTokenUsage = async () => {
        try {
          setTokenUsageLoading(true);
          const response = await gradeApi.get({
            path: `/api/account/token-usage`,
            config: { headers: { Authorization: `Bearer ${user.session_token}` } }
          });
          setTokenUsage(response.data.data);
        } catch (error) {
          console.log('Error fetching token usage:', error);
        } finally {
          setTokenUsageLoading(false);
        }
      };

      const getSubscriptionData = async () => {
        try {
          const response = await gradeApi.get({
            path: `/api/subscription/current`,
            config: { headers: { Authorization: `Bearer ${user.session_token}` } }
          });
          setSubscriptionData(response.data);
        } catch (error) {
          console.log('Error fetching subscription data:', error);
        }
      };

      getRegrades();
      getTokenUsage();
      getSubscriptionData();
    }
  }, [user]);

  const isActiveTab = tabName => tabName === activePage;

  const handleLogout = () => setIsLogoutModalOpen(true);
  const confirmLogout = () => {
    logout();
    setIsLogoutModalOpen(false);
  };

  const handleUpgradeSubscription = async () => {
    try {
      setIsUpgrading(true);
      const response = await gradeApi.post({
        path: `/api/subscription/create-checkout`,
        config: {
          headers: { Authorization: `Bearer ${user.session_token}` }
        }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const formatDate = dateString => {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const SidebarItem = ({ to, icon, activeIcon, label, tabName }) => (
    <Link to={to} className={`sidebar-item ${isActiveTab(tabName) ? 'active' : ''}`}>
      <img
        src={isActiveTab(tabName) ? activeIcon : icon}
        alt={label}
        className="sidebar-item-icon"
      />
      <span className="sidebar-item-label">{label}</span>
      {tabName === 'Regrade' && regrades && <div className="notification-dot"></div>}
    </Link>
  );

  const TokenUsageBar = () => {
    const creditsRemaining = Math.max(tokenUsage.credit_limit - tokenUsage.credits_used, 0);
    const percentageRemaining = Math.max((creditsRemaining / tokenUsage.credit_limit) * 100, 0);

    let barColor;
    if (percentageRemaining < 20) {
      barColor = '#ef4444';
    } // red-500
    else if (percentageRemaining < 50) {
      barColor = '#f97316';
    } // orange-500
    else {
      barColor = '#22c55e';
    } // green-500

    // Don't show subscription info until data is loaded
    if (!subscriptionData) {
      return (
        <div className="token-usage-bar">
          <div className="header" style={{ marginBottom: '8px' }}>
            <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#6b7280',
                  display: 'inline-block'
                }}
              ></span>
              Loading...
            </div>
          </div>
          <div className="header">
            <div className="title">AI Credits</div>
            <div className="usage">Loading...</div>
          </div>
          <div className="bar-background">{/* Show empty bar during loading */}</div>
        </div>
      );
    }

    const currentPlan = subscriptionData?.subscription?.plan || 'free';
    const isFreePlan = currentPlan === 'free';
    const isPro = currentPlan === 'pro';
    const isUnlimited = currentPlan === 'unlimited';

    return (
      <div className="token-usage-bar">
        {/* Subscription Info Header */}
        <div className="header" style={{ marginBottom: '8px' }}>
          <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: isFreePlan ? '#6b7280' : isPro ? '#e4b17a' : '#10b981',
                display: 'inline-block'
              }}
            ></span>
            {isFreePlan ? 'Free Plan' : isPro ? 'Pro Plan' : 'Unlimited Plan'}
          </div>
          {isPro && subscriptionData?.subscription?.current_period_end && (
            <div className="text-xs" style={{ color: '#9ca3af' }}>
              {subscriptionData?.subscription?.cancel_at_period_end ? 'Ends' : 'Renews'}:{' '}
              {formatDate(subscriptionData.subscription.current_period_end)}
            </div>
          )}
        </div>

        {/* Credits Usage */}
        <div className="header">
          <div className="title">AI Credits</div>
          <div className="usage">
            {tokenUsageLoading
              ? 'Loading...'
              : isUnlimited
                ? 'Unlimited'
                : `${creditsRemaining} / ${tokenUsage.credit_limit}`}
          </div>
        </div>
        <div className="bar-background">
          {!tokenUsageLoading && (
            <div
              className="bar-foreground"
              style={{
                width: isUnlimited ? '100%' : `${percentageRemaining}%`,
                backgroundColor: isUnlimited ? '#22c55e' : barColor
              }}
            />
          )}
        </div>

        {/* Upgrade Button for Free Users */}
        {isFreePlan && (
          <button
            disabled={isUpgrading}
            className="upgrade-button"
            style={{
              width: '100%',
              marginTop: '8px',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: isUpgrading ? 'not-allowed' : 'pointer',
              opacity: isUpgrading ? 0.7 : 1,
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 4px rgba(228, 177, 122, 0.2)'
            }}
            onClick={handleUpgradeSubscription}
            onMouseEnter={e => {
              if (!isUpgrading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(228, 177, 122, 0.3)';
              }
            }}
            onMouseLeave={e => {
              if (!isUpgrading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(228, 177, 122, 0.2)';
              }
            }}
          >
            {isUpgrading ? 'Processing...' : 'Upgrade to Pro'}
          </button>
        )}

        {/* Manage Subscription Button for Pro Users */}
        {isPro && (
          <Link
            to="/profile"
            className="manage-button"
            style={{
              display: 'block',
              width: '100%',
              marginTop: '8px',
              padding: '8px 12px',
              background: 'white',
              color: '#8c6b4a',
              border: '1px solid rgba(228, 177, 122, 0.3)',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              textAlign: 'center',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              boxShadow: '0 1px 2px rgba(228, 177, 122, 0.1)'
            }}
            onMouseEnter={e => {
              e.target.style.background = 'rgba(228, 177, 122, 0.05)';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 2px 4px rgba(228, 177, 122, 0.2)';
            }}
            onMouseLeave={e => {
              e.target.style.background = 'white';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 1px 2px rgba(228, 177, 122, 0.1)';
            }}
          >
            Manage Subscription
          </Link>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="w-64 h-screen bg-white border-r border-gray-200 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        message="Are you sure you want to log out?"
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={confirmLogout}
      />
      <div id="sidebar">
        <div>
          <Link to={user.user.is_teacher ? '/tclasses' : '/sclasses'} className="logo-container">
            <img src="/assets/logo_icon.png" alt="GradeWiz Icon" className="logo-icon" />
            <span className="logo-text">GradeWiz</span>
            <div className="beta-tag">Beta</div>
          </Link>

          <nav className="nav-items">
            <SidebarItem
              to={user.user.is_teacher ? '/tclasses' : '/sclasses'}
              icon="/assets/sidebar/menu-home.png"
              activeIcon="/assets/sidebar/menu-home-white.png"
              label="My Classes"
              tabName="Home"
            />
            {user.user.is_teacher && (
              <SidebarItem
                to="/regrade"
                icon="/assets/sidebar/quote-request.png"
                activeIcon="/assets/sidebar/quote-request-white.png"
                label="Regrade Requests"
                tabName="Regrade"
              />
            )}
            <SidebarItem
              to="/support"
              icon="/assets/sidebar/support.png"
              activeIcon="/assets/sidebar/support-white.png"
              label="Support & Feedback"
              tabName="Support"
            />
          </nav>
        </div>

        <div className="sidebar-footer">
          <TokenUsageBar />
          <div className="profile-section">
            <div className="user-info">
              <img src="/assets/sidebar/Profile.png" alt="Profile" className="profile-icon" />
              <div className="user-details">
                <div className="user-name">
                  {user.user.first_name} {user.user.last_name}
                </div>
                <div className="user-id">ID: {user.user.id}</div>
              </div>
            </div>
            <div className="profile-actions">
              <Link to="/profile" className="action-button">
                <img
                  src="/assets/sidebar/account_settings.png"
                  className="action-icon"
                  alt="Account Settings"
                />
              </Link>
              <button className="action-button" onClick={handleLogout}>
                <img src="/assets/sidebar/logout.png" className="action-icon" alt="Logout" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SideBar;
